import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
} from "@mui/material"
import React, { FC, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { format } from "date-fns"
import { useChannelsQuery } from "../../services/channels/channelsApi"
import { useLazyGetMailingCredentialsQuery } from "../../services/mailing/postBotMailingApi"
import AddNew from "./addNew"
import ChannelCard from "./channelCard"
import { SnackbarAlert } from "@/core/components"
import { useAppDispatch } from "@/modules/app/hooks"
import { useAuth } from "@/modules/app/hooks"
import { setMailingPrefill, setUsers } from "@/modules/telegram/store"
import { App } from "@/modules/app/interfaces"
import { hasModuleAccess } from "@/modules/app/utils"

interface IChannelsListProps {}

const ChannelsList: FC<IChannelsListProps> = (props) => {
  const [withHidden, setWithHidden] = useState(false)
  const {
    isLoading,
    isError,
    currentData: data,
  } = useChannelsQuery({
    queryParams: {
      withHidden,
    },
  })

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const auth = useAuth()
  const [triggerGetCredentials, { isFetching: isFetchingCreds }] =
    useLazyGetMailingCredentialsQuery()
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    variant: "error" | "warning"
    message: string
  }>({ open: false, variant: "error", message: "" })

  const canCreateMailing = useMemo(() => {
    if (!auth) return false
    if (auth.type === App.AuthType.OIDC) {
      const userInfo = auth.userInfo as App.OIDCUserInfo | undefined
      const userRoles = userInfo?.roles || []
      return hasModuleAccess(userRoles, App.ModuleName.TG_MAILING)
    }
    // STANDARD: tied to UserRole (Mailings route requires MANAGER+)
    return (auth.role ?? App.UserRole.ANON) >= App.UserRole.MANAGER
  }, [auth])

  const handleCreateMailing = async () => {
    try {
      const res = await triggerGetCredentials().unwrap()
      if (!res.pairs.length) {
        setSnackbar({
          open: true,
          variant: "warning",
          message: "Нет активных каналов для рассылки",
        })
        return
      }
      const users = res.pairs.map((p) => `${p.token}/${String(p.chatId)}`)
      dispatch(setUsers(users))
      dispatch(
        setMailingPrefill({
          name: `Рассылка из PostBot ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
        }),
      )
      navigate("/telegram/mailings/new")
    } catch (e) {
      setSnackbar({
        open: true,
        variant: "error",
        message: "Не удалось получить данные для рассылки",
      })
    }
  }

  return (
    <>
      {isError && (
        <Alert severity="error">
          Во время загрузки списка каналов произошла ошибка!
        </Alert>
      )}
      {isLoading && <LinearProgress />}
      <SnackbarAlert
        isOpen={snackbar.open}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        variant={snackbar.variant}
        message={snackbar.message}
      />
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <FormControlLabel
            value={withHidden}
            control={
              <Switch
                onChange={(e, value) => setWithHidden(value)}
                inputProps={{ "aria-label": "Показать скрытые" }}
              />
            }
            label="Показать скрытые"
            labelPlacement="start"
          />
        </Grid>
        {canCreateMailing && (
          <Grid item>
            <Button
              variant="outlined"
              onClick={handleCreateMailing}
              disabled={isFetchingCreds}
              startIcon={
                isFetchingCreds ? <CircularProgress size={16} /> : undefined
              }
            >
              Создать рассылку
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} p={2}>
        {!!data?.length &&
          data.map((ch) => (
            <Grid item xs={12} md={6} xl={4} key={`${ch.id}`}>
              <ChannelCard {...ch} />
            </Grid>
          ))}
        {!data?.length && !isError && !isLoading && (
          <Grid item xs={12} md={6} xl={4}>
            <Alert
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "100px",
                display: "flex",
                padding: 0,
                alignItems: "center",
                justifyContent: "center",
              }}
              severity="info"
            >
              Каналов нет
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} md={6} xl={4}>
          <AddNew />
        </Grid>
      </Grid>
    </>
  )
}

export default ChannelsList
